import { mat4, vec3 } from 'gl-matrix';
import { DcmJsDatasetWrapper } from '~/domain/dicom/dcmJsWrapper';
import { DicomRigidRegistration } from '~/domain/dicom/dicomRigidRegistration';
import type { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { VoiBounds } from '~/state/atoms/toolsAtoms';
import { generateUID } from '~/utils/dicomUid';
import { Grid } from '../../engine/data/common/Grid';
import { runElastixRegistration } from './elastix';
import { Image3DArray } from './image3DArray';

/**
 * Find the spanning box of a grid in world space. Useful when grids axis are not oblique
 *
 * @param grid  - grid to find spanning box for
 * @returns min and max points of the grid in world space
 */
function findSpanningBoxForGrid(grid: Grid): { min: vec3; max: vec3 } {
    const min = grid.voxelCenterToWorldSpace(vec3.fromValues(-0.5, -0.5, -0.5));
    const max = grid.voxelCenterToWorldSpace(
        vec3.sub(vec3.create(), grid.getSize(), vec3.fromValues(0.5, 0.5, 0.5))
    );
    const newMin = vec3.min(vec3.create(), min, max);
    const newMax = vec3.max(vec3.create(), min, max);
    return { min: newMin, max: newMax };
}

/**
 * Find the spanning box for a box defined by min and max points.
 * Needed, to find a new box after applying a transform.
 *
 * @param box - box with min and max points
 * @returns new box with min and max points defining the spanning box
 */
function findSpanningBoxForBox(box: { min: vec3; max: vec3 }): {
    min: vec3;
    max: vec3;
} {
    const newMin = vec3.min(vec3.create(), box.min, box.max);
    const newMax = vec3.max(vec3.create(), box.min, box.max);
    return { min: newMin, max: newMax };
}

/**
 * Get target resolution for image registration.
 * Either returns the provided target resolution or computes it as the maximum resolution between the two images.
 * After the resolution is determined, an optional rescale factor is applied.
 * This function gives the same result if fixed and moving images are swapped.
 *
 * @param fixedImage - one of the images to be registered
 * @param movingImage - another image to be registered
 * @param tgtRes - optional target resolution
 * @param rescaleFactor - optional rescale factor for the target resolution
 * @returns target resolution to be used for registration
 */
function getTargetResolution(
    fixedImage: Image3DArray,
    movingImage: Image3DArray,
    tgtRes?: vec3 | null,
    rescaleFactor: number = 1.0
): vec3 {
    let tgtResFinal: vec3;
    if (!tgtRes) {
        // If target resolution is not provided, use maximum resolution between the two images
        tgtResFinal = vec3.max(
            vec3.create(),
            fixedImage.grid.getRes(),
            movingImage.grid.getRes()
        );
    } else {
        // otherwise use provided target resolution
        tgtResFinal = tgtRes;
    }

    if (rescaleFactor < 1.0 && rescaleFactor > 0.0) {
        return vec3.scale(vec3.create(), tgtResFinal, rescaleFactor);
    } else if (rescaleFactor === 1.0) {
        return tgtResFinal;
    } else {
        throw new Error('Rescale factor must be positive');
    }
}

/**
 * Generate grid for registration based on target resolution and VOI bounds.
 *
 * Grid's physical size and origin are determined as follows:
 *
 * 1. If VOI bounds are provided, they are used to define the grid's physical size and origin.
 * 2. If VOI bounds are not provided is inferred from  fixed and moving images grid and initial transform.
 * First, spanning box (physical limits) is found for both fixed and moving image. Then
 * initial transform is applied to moving image's spanning box and overlap between it fixed image's
 * spanning box is used to define new grid's physical size and origin.
 *
 * Grids voxel size is derived from provided target resolution and the physical size.
 *
 * The resulting grid is always axis-aligned.
 *
 * @param fixedImage - fixed image
 * @param movingImage - moving image
 * @param initTransform - initial transformation matrix, applied to moving image's grid
 * @param tgtRes - target resolution
 * @param VOIbounds - optional volume of interest bounds
 * @returns grid for registration
 */

export function getGridForRegistration(
    fixedImage: Image3DArray,
    movingImage: Image3DArray,
    initTransform: mat4,
    tgtRes: vec3,
    VOIbounds: VoiBounds | null = null
): Grid {
    if (VOIbounds) {
        // derive grid from VOI bounds
        return new Grid(
            Math.ceil((VOIbounds.maxX - VOIbounds.minX) / tgtRes[0]),
            Math.ceil((VOIbounds.maxY - VOIbounds.minY) / tgtRes[1]),
            Math.ceil((VOIbounds.maxZ - VOIbounds.minZ) / tgtRes[2]),
            tgtRes[0],
            tgtRes[1],
            tgtRes[2],
            mat4.fromTranslation(mat4.create(), [
                VOIbounds.minX,
                VOIbounds.minY,
                VOIbounds.minZ,
            ])
        );
    } else {
        // find overlapping box between fixed and moving images
        // This is needed  to cover the case when grids are not axis-aligned
        // Spanning boxes are axis-aligned
        const fixedBox = findSpanningBoxForGrid(fixedImage.grid);
        const movingBox = findSpanningBoxForGrid(movingImage.grid);
        // Transform moving box with initial transform
        const trMovingBox = {
            min: vec3.transformMat4(
                vec3.create(),
                movingBox.min,
                initTransform
            ),
            max: vec3.transformMat4(
                vec3.create(),
                movingBox.max,
                initTransform
            ),
        };

        // If the translation included rotation, we need to find the spanning box again
        const trMovingBoxCorrected = findSpanningBoxForBox(trMovingBox);

        // Find overlap between fixed box and transformed moving box
        const newMin = vec3.max(
            vec3.create(),
            fixedBox.min,
            trMovingBoxCorrected.min
        );
        const newMax = vec3.min(
            vec3.create(),
            fixedBox.max,
            trMovingBoxCorrected.max
        );
        return new Grid(
            Math.ceil((newMax[0] - newMin[0]) / tgtRes[0]),
            Math.ceil((newMax[1] - newMin[1]) / tgtRes[1]),
            Math.ceil((newMax[2] - newMin[2]) / tgtRes[2]),
            tgtRes[0],
            tgtRes[1],
            tgtRes[2],
            mat4.fromTranslation(mat4.create(), [
                newMin[0],
                newMin[1],
                newMin[2],
            ])
        );
    }
}

/**
 * Create a RigidRegistration object compatible with DICOM standard.
 * Assumes that the transform matrix is already generated and
 * maps points from secondary image space to primary image space.
 *
 * @param primaryImage - primary (fixed/reference) image
 * @param secondaryImage - secondary (moving) image
 * @param transformMatrix  - transformation 4x4 matrix.
 * @param contentLabel - optional content label for the registration
 * @returns RigidRegistration object
 */
function createRigidRegistrationObject(
    primaryImage: Image3D,
    secondaryImage: Image3D,
    transformMatrix: mat4,
    contentLabel: string = ''
): RigidRegistration {
    const sopInstanceUID = generateUID();
    const dataset = new DcmJsDatasetWrapper({
        SOPInstanceUID: sopInstanceUID,
        Modality: 'REG',
        ContentLabel: contentLabel,
    });

    const dicomParent = new DicomRigidRegistration(
        sopInstanceUID,
        primaryImage.frameOfReferenceUid,
        secondaryImage.frameOfReferenceUid,
        contentLabel,
        transformMatrix,
        dataset
    );

    return new RigidRegistration(
        primaryImage.frameOfReferenceUid,
        secondaryImage.frameOfReferenceUid,
        transformMatrix,
        dicomParent
    );
}

/**
 * Perform rigid image registration using Elastix.
 * Before the registration both images are resampled
 * to a common grid derived from fixed and moving images parameters,
 * as well as from optional initial transform, target resolution
 * and  VOI bounds.
 *
 * The initial and returned transforms are a assumed to map secondary(moving) image
 * space to primary(fixed) image space.
 *
 * @param primaryImage - primary (fixed/reference) Image3D
 * @param secondaryImage - secondary (moving) Image3D (will be aligned to primary)
 * @param initialTransform - Optional initial 4x4 transformation matrix (mat4) to use as starting point
 * @param voiBounds - Optional volume of interest bounds to limit registration area
 * @param targetResolution - Optional target resolution (voxel size) for resampling before registration
 * @param rescaleFactor - Optional rescale factor to apply to images before registration
 * @param elastixSettings - Optional elastix settings to customize registration process
 * @returns Registration result with registered image and transform parameters
 */
export async function performRigidRegistration(
    primaryImage: Image3D,
    secondaryImage: Image3D,
    initialTransform: mat4 = mat4.create(),
    voiBounds: VoiBounds | null = null,
    targetResolution: vec3 | null = null,
    rescaleFactor: number = 1.0,
    elastixSettings: Record<string, string> = {}
): Promise<RigidRegistration> {
    // Convert to Image3DArray for resampling operations
    const fixedImage = Image3DArray.fromImage3D(primaryImage);
    const movingImage = Image3DArray.fromImage3D(secondaryImage);

    // determine target resolution for registration
    const tgtRes = getTargetResolution(
        fixedImage,
        movingImage,
        targetResolution,
        rescaleFactor
    );

    // derive target grid for registration
    const tgtGrid = getGridForRegistration(
        fixedImage,
        movingImage,
        initialTransform,
        tgtRes,
        voiBounds
    );

    const resampledFixedImage = fixedImage.resampleFast(tgtGrid);

    const resampledMovingImage = movingImage.resampleFast(
        tgtGrid,
        initialTransform
    );

    // run elastix registration on resampled images
    const elastixTransform = await runElastixRegistration(
        resampledFixedImage,
        resampledMovingImage,
        elastixSettings
    );

    // combine elastix transform with initial transform
    const finalMatrix = mat4.multiply(
        mat4.create(),
        elastixTransform,
        initialTransform
    );

    // create RigidRegistration object compatible with DICOM standard
    return createRigidRegistrationObject(
        primaryImage,
        secondaryImage,
        finalMatrix,
        'Auto Registration'
    );
}
